import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    LoadingIndicator,
    PageSplitDescription,
    PageSplitTitle,
} from '@gitbook/spine-react';
import { useHeadTitle } from '@gitbook/util-react-head';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { MFAScreenModal, useHideSplashscreen } from '../../Components';
import { useAuthOperations } from '../../state';
import { LoginScreenLayout } from './LoginScreenLayout';

/**
 * Step to authenticate directly with GitHub. Used when:
 * - User clicks on the "Sign in with GitHub" button on the marketing page.
 * - User installs the GitHub App from the marketplace.
 */
export function StepRedirectToGitHubAuth(): React.ReactElement {
    const { mfa, signInWithGitHub, redirectToGitHubAuth } = useAuthOperations();

    const [loading, wrapLoading] = useLoadingState();

    useHideSplashscreen(0);
    useHeadTitle('Authenticating with GitHub…');

    React.useEffect(() => {
        wrapLoading(() => redirectToGitHubAuth());
    }, [wrapLoading, redirectToGitHubAuth]);

    const onPressSignIn = () => {
        wrapLoading(() => signInWithGitHub());
    };

    if (mfa) {
        return <MFAScreenModal mfa={mfa} />;
    }

    return (
        <LoginScreenLayout>
            <PageSplitTitle>Authenticating with GitHub…</PageSplitTitle>
            <PageSplitDescription>
                A popup will open to authenticate with your GitHub account. You can also click the
                login button below.
            </PageSplitDescription>
            <Button
                leadingIcon={loading ? LoadingIndicator : Icon.GitHub}
                kind="secondary"
                size="large"
                disabled={loading}
                onPress={onPressSignIn}
                label="Sign in with GitHub"
            />
        </LoginScreenLayout>
    );
}
